'use no memo';
import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {DottedIconButton} from '@Components/dotted-icon-button';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {SlideshowListOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-list-option';
import './contextual-menu-slides-option.scss';
import {Dropdown} from '@Components/dropdown-v2';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {AddItemThumbnail} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-thumbnail';
import {onOpenSlideshowStockMediaModal, onOpenUploadsMediaSlideshowModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {SlideshowIntroDelayOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-intro-delay-option';
import styles from './contextual-menu-slides-options.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {noop} from '@Utils/general.util';

interface ContextualMenuSlidesOptionsProps {
  id: string;
  className?: string;
  introDelay: number;
  numberOfItems: number;

  onChangeIntroDelay(val: number): void;

  onChangeIntroDelayEnded(val: number): void;
}

export function ContextualMenuSlidesOptions({...props}: ContextualMenuSlidesOptionsProps): ReactElement<any> {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isElementMounted, setIsElementMounted] = useState(false);
  const {topShadow, bottomShadow} = useTopBottomScrollShadow(scrollContainerRef, isElementMounted);
  const [height, setHeight] = useState((props.numberOfItems + 1) * 56);
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;

  useEffect(() => {
    if (!isElementMounted) {
      setIsElementMounted(true);
    }
  }, []);

  useEffect((): void => {
    setHeight((props.numberOfItems + 1) * 56);
  }, [props.numberOfItems]);

  const getNewSlideOptions = (): ControlledListItemType[] => {
    return [
      {
        id: 'new-text-slide',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_text'),
        subText: window.i18next.t('pmwjs_add_text_slide'),
        onClick: (): void => {
          if (activeItem?.isSlideshow()) {
            void activeItem.addTextSlide();
          }
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: <AddItemThumbnail className={styles.thumbnailWidth} icon="icon-text" />,
        },
      },
      {
        id: 'new-upload-media-slide',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_uploads'),
        subText: window.i18next.t('pmwjs_add_uploads_media_slide'),
        onClick: (): void => {
          if (activeItem?.isSlideshow()) {
            onOpenUploadsMediaSlideshowModal(activeItem.prepareAndAddMediaSlides.bind(activeItem));
          }
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: <AddItemThumbnail className={styles.thumbnailWidth} icon="icon-upload" />,
        },
      },
      {
        id: 'new-stock-media-slide',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_media'),
        subText: window.i18next.t('pmwjs_add_stock_media_slide'),
        onClick: (): void => {
          if (activeItem?.isSlideshow()) {
            onOpenSlideshowStockMediaModal(activeItem.prepareAndAddMediaSlides.bind(activeItem));
          }
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: <AddItemThumbnail className={styles.thumbnailWidth} iconClassName={styles.icon} icon="icon-media" />,
        },
      },
    ];
  };

  return (
    <li className={styles.mainContainer}>
      <div
        style={{
          height: `${height}px`,
        }}
        className={styles.slideShowListWrapper}
      >
        <div
          id="slideshow-list-container"
          ref={scrollContainerRef}
          className={`${styles.slideshowListContainer} ${topShadow ? styles.topShadow : ''} ${bottomShadow ? styles.bottomShadow : ''}`}
        >
          <SlideshowIntroDelayOption
            introDelay={props.introDelay}
            onChangeIntroDelay={(val: number): void => {
              props.onChangeIntroDelay(val);
            }}
            onChangeIntroDelayEnded={(val: number): void => {
              props.onChangeIntroDelayEnded(val);
            }}
          />
          <div>
            <SlideshowListOption />
          </div>
        </div>
      </div>

      <Dropdown
        className={`spacing-p-t-3 ${styles.addNewSlideBtn} spacing-m-l-9`}
        popupClassName={styles.newSlidePopupWidth}
        popUpHasCustomWidth
        selector={<DottedIconButton btnIcon="icon-plus" className="add-slideshow-dotted-button -horizontal" btnText="" onClick={noop} />}
        popup={<ControlledList className="spacing-m-0 _full-width" id="slide-options" items={getNewSlideOptions()} />}
      />
    </li>
  );
}
